import { httpApi } from '@api/http.api.ts';
import type { CommonTableRequest, CreatorRevision } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CreatorElement,
    ItemQuizQuestion,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/api/creatorElements.api.ts';
import type {
    PATHWAY_CATEGORIES,
    PATHWAY_TYPES,
} from '@routes/sidebar/admin-center/creator/___pages___/pathways/ui/form/constants.ts';
import type { PATHWAYS_STATUSES } from '@routes/sidebar/admin-center/creator/___pages___/pathways/ui/tables/pathways-table/constants.ts';
import type {
    MatrixRankScope,
    MatrixVesselScope,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/api/matrices.api.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';

export type PathwayCategory = (typeof PATHWAY_CATEGORIES)[keyof typeof PATHWAY_CATEGORIES];
export type PathwayType = (typeof PATHWAY_TYPES)[keyof typeof PATHWAY_TYPES];
export type PathwaysStatusesType = (typeof PATHWAYS_STATUSES)[keyof typeof PATHWAYS_STATUSES];

export interface PathwaySectionElementRequest {
    id: number;
    elementOrder: number;
    isPracticeQuizEnabled: boolean;
}

export interface PathwaySectionRequest {
    id: number;
    parentSectionId: number | null;
    adminPathwaySectionId: string | null;
    sectionName: string;
    sectionOrder: number;
    subSections: PathwaySectionRequest[];
    elements: PathwaySectionElementRequest[];
}

export interface ElementApiResponse {
    elementOrder: number;
    isPracticeQuizEnabled: boolean;
    elementAdmin: {
        id: number;
        currentPublishedElementId: number | null;
        timeInSecondsPerQuestion: number;
        revisions: CreatorRevision[];
        elementAdminInfo: CreatorElement;
        usedInPathways: CreatorPathway[];
        itemQuizQuestions?: ItemQuizQuestion[];
    };
}

export interface PathwaySectionResponse {
    id: number;
    adminPathwaySectionId: string;
    parentSectionId: number | null;
    sectionName: string;
    sectionOrder: number;
    subSections: PathwaySectionResponse[];
    elements: ElementApiResponse[];
    totalElements: number;
}

export interface CreatorContent {
    bucket: string | null;
    contentType: string | null;
    fileName: string | null;
    contentFileName: string | null;
    outputUrl: string | null;
    isPublic: boolean;
    objectSize: number;
    contentId: string;
    providerType: string;
    checkSum: number;
    contentMinutesDuration: number;
}

export interface CreatorPathway {
    id: number;
    pathwayId: string | null;
    currentPublishedPathwayId: number | null;
    title: string;
    creatorReference: string | null;
    createdBy: string;
    createdFor: string | null;
    createdByDisplayName?: string | null;
    createdForDisplayName?: string | null;
    category: PathwayCategory;
    pathwayType: PathwayType;
    cardDescription: string;
    description: string;
    cardImageContentId: string;
    cardImageURI: string;
    status: PathwaysStatusesType | null;
    isSequential: boolean;
    passingPercentage: number;
    latestRevision: string | null;
    elementCount: number;
    hasPracticeQuiz: boolean;
    hasFinalQuiz: boolean;
    isFinalQuizEnabled: boolean;
    isOrganizedInSections: boolean;
    activeSubscriptions: number;
    totalTimeInSeconds: number | null;
    taskCount: number | null;
    regulations: string[];
    createdAtUTC: string | null;
    updatedAtUTC: string | null;
    publishedAtUTC: string | null;
    accreditations: string[];
    vessels: MatrixVesselScope | null;
    competences: string[];
    ranks: MatrixRankScope | null;
    revisions: CreatorRevision[];
    departmentsLevels: string[];
    hasCertificate: boolean;
    certificateText: string | null;
    disableSeekingOnFirstPlay: boolean;
    learningObjectives: string[] | null;
    adminPathwayId: string | null;
    content: CreatorContent | null;
}

export interface CreatorPathwayResponse extends CreatorPathway {
    sections: PathwaySectionResponse[];
}

export interface PostPathwayRequest {
    title: string;
    creator: string;
    creatorReference?: string | null;
    status?: PathwaysStatusesType | null;
    createdFor?: string | null;
    pathwayType: string | null;
    category: string | null;
    cardDescription?: string | null;
    description?: string | null;
    cardImageContentId?: string | null;
    isSequential?: boolean;
    passingPercentage: number;
    sections: PathwaySectionRequest[];
    accreditations: string[];
    regulations: string[];
    vessels: MatrixVesselScope | null;
    competences: string[];
    ranks: MatrixRankScope | null;
    departmentsLevels: string[];
    isFinalQuizEnabled: boolean;
    isOrganizedInSections: boolean;
    revisionNotes?: string;
    creatorComments?: string;
    hasCertificate: boolean;
    certificateText: string | null;
    disableSeekingOnFirstPlay: boolean;
}

export interface PutPathwayRequest extends Omit<PostPathwayRequest, 'sections'> {
    sections?: PathwaySectionRequest[];
}

interface DeleteCreatorPathwayById {
    id: string;
    comment: string | null;
}

export type CommonCreatorPathwaysRequest = CommonTableRequest;

const BASE_URL = 'admin-center/pathways';

export const getApiCreatorPathways = (
    payload: CommonCreatorPathwaysRequest,
): Promise<PaginatedResponse<CreatorPathway, EmptyResultsDescription>> =>
    httpApi
        .post<PaginatedResponse<CreatorPathway, EmptyResultsDescription>>(`${BASE_URL}/search`, payload)
        .then(({ data }) => data);

export const getApiCreatorPathwayById = (pathwayId: string): Promise<CreatorPathwayResponse> =>
    httpApi.get<CreatorPathwayResponse>(`${BASE_URL}/${pathwayId}`).then(({ data }) => data);

export const createApiCreatorPathway = (payload: PostPathwayRequest): Promise<CreatorPathwayResponse> =>
    httpApi.post<CreatorPathwayResponse>(BASE_URL, payload).then(({ data }) => data);

export const updateApiCreatorPathway = (id: string, payload: PutPathwayRequest): Promise<CreatorPathwayResponse> =>
    httpApi.put<CreatorPathwayResponse>(`${BASE_URL}/${id}`, payload).then(({ data }) => data);

export const deleteApiCreatorPathway = ({ id, comment }: DeleteCreatorPathwayById): Promise<any> =>
    httpApi.delete(`${BASE_URL}/${id}`, { data: { comment } }).then(({ data }) => data);
